import { useState, useEffect, useCallback } from 'react'
import { Tv, Image as ImageIcon, ShieldCheck, Sliders, Monitor } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import RokuImageMigration from './RokuImageMigration'
import RokuFeedValidator from './RokuFeedValidator'
import './RokuSettings.css'

const BASE_DEFAULTS = {
  roku_search_include_live: '0',
  roku_search_include_drafts: '0',
  roku_search_min_duration: '0',
  roku_search_exclude_tags: '',
  // Roku app runtime settings (delivered to the app via watch /api/config → roku section).
  roku_screensaver_idle_enabled: '1',  // cert-safe default: ON
  roku_screensaver_idle_seconds: '40', // cert-safe default: < Roku's ~60s screensaver timeout
}
const MANAGED_KEYS = Object.keys(BASE_DEFAULTS)

export default function RokuSettings() {
  const { api } = useAuth()
  const [tab, setTab] = useState('options') // 'options' | 'app' | 'migration' | 'validator'
  const [settings, setSettings] = useState({ ...BASE_DEFAULTS })
  const [allTags, setAllTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [data, tagsData] = await Promise.all([
        api('/settings?prefix=roku_'),
        api('/tags').catch(() => ({ tags: [] })),
      ])
      const s = data.settings || {}
      setSettings(prev => ({ ...prev, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null && v !== '')) }))
      setAllTags(tagsData.tags || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  function set(key, value) { setSettings(prev => ({ ...prev, [key]: value })) }

  const excluded = (settings.roku_search_exclude_tags || '').split(',').map(t => t.trim()).filter(Boolean)
  function toggleTag(t) {
    const next = excluded.includes(t) ? excluded.filter(x => x !== t) : [...excluded, t]
    set('roku_search_exclude_tags', next.join(','))
  }

  async function save() {
    setSaving(true); setStatus('Saving…')
    try {
      const payload = {}
      for (const k of MANAGED_KEYS) payload[k] = String(settings[k] ?? '')
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('✅ Saved. Feed reflects changes within ~5 min (edge/KV cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="roku-settings">
      <div className="roku-settings-header">
        <h1><Tv size={22} /> Roku Settings</h1>
        <span className="status-text">{status}</span>
      </div>

      <div className="rs-tabs">
        <button className={`rs-tab${tab === 'options' ? ' active' : ''}`} onClick={() => setTab('options')}>
          <Sliders size={15} /> Search Feed Options
        </button>
        <button className={`rs-tab${tab === 'app' ? ' active' : ''}`} onClick={() => setTab('app')}>
          <Monitor size={15} /> App Settings
        </button>
        <button className={`rs-tab${tab === 'migration' ? ' active' : ''}`} onClick={() => setTab('migration')}>
          <ImageIcon size={15} /> Image Migration
        </button>
        <button className={`rs-tab${tab === 'validator' ? ' active' : ''}`} onClick={() => setTab('validator')}>
          <ShieldCheck size={15} /> Feed Validator
        </button>
      </div>

      {tab === 'options' && (
        <div className="rs-card">
          <h2>Roku search feed — what to include</h2>
          <p className="rs-hint">
            Controls <code>/api/roku-search-feed</code>. Defaults keep the feed VOD-only, published, unfiltered.
          </p>
          {loading ? <div className="rs-loading">Loading…</div> : (
            <>
              <label className="rs-field rs-check">
                <input type="checkbox" checked={settings.roku_search_include_live === '1'}
                  onChange={e => set('roku_search_include_live', e.target.checked ? '1' : '0')} />
                <span>
                  <strong>Include live channels</strong>
                  <em>Adds the linear channels to Roku search. ⚠ The Roku app resolves live only on
                  <code>mediaType=channel</code> and linear has no duration — these won't deep-link (or pass
                  Roku validation) until the Roku-side change ships.</em>
                </span>
              </label>

              <label className="rs-field rs-check">
                <input type="checkbox" checked={settings.roku_search_include_drafts === '1'}
                  onChange={e => set('roku_search_include_drafts', e.target.checked ? '1' : '0')} />
                <span>
                  <strong>Include drafts / unpublished</strong>
                  <em>Bypasses the <code>/api/vod</code> intersection. ⚠ Draft deep-links won't resolve in the
                  Roku app — useful for preview/testing only.</em>
                </span>
              </label>

              <div className="rs-field">
                <label className="rs-label"><strong>Minimum duration (seconds)</strong></label>
                <input type="number" min="0" step="1" className="rs-input"
                  value={settings.roku_search_min_duration || '0'}
                  onChange={e => set('roku_search_min_duration', e.target.value)} />
                <em className="rs-sub">Drop assets shorter than this (0 = no filter). E.g. exclude short lead-ins/IDs.</em>
              </div>

              <div className="rs-field">
                <label className="rs-label"><strong>Exclude tags</strong></label>
                <em className="rs-sub">Items carrying any selected tag are dropped from the search feed.</em>
                {allTags.length === 0 ? <div className="rs-sub">No tags found.</div> : (
                  <div className="rs-tagchips">
                    {allTags.map(t => (
                      <button type="button" key={t}
                        className={`rs-chip${excluded.includes(t) ? ' on' : ''}`}
                        onClick={() => toggleTag(t)}>{t}</button>
                    ))}
                  </div>
                )}
              </div>

              <button className="cmpl-btn cmpl-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save feed options'}
              </button>
            </>
          )}
        </div>
      )}

      {tab === 'app' && (
        <div className="rs-card">
          <h2>Roku app — runtime settings</h2>
          <p className="rs-hint">
            Delivered to the Roku app via <code>/api/config</code> (the <code>roku</code> section). Defaults keep
            today's behavior on any config miss.
          </p>
          {loading ? <div className="rs-loading">Loading…</div> : (
            <>
              <label className="rs-field rs-check">
                <input type="checkbox" checked={settings.roku_screensaver_idle_enabled !== '0'}
                  onChange={e => set('roku_screensaver_idle_enabled', e.target.checked ? '1' : '0')} />
                <span>
                  <strong>Screensaver idle-stop</strong>
                  <em>Stops idle menu previews so Roku's system screensaver can activate. ⚠ <strong>Required for
                  Roku certification.</strong> Turning this OFF (or setting the delay above ~55s) will fail the
                  screensaver check — Off is for internal/demo builds only.</em>
                </span>
              </label>

              <div className="rs-field">
                <label className="rs-label"><strong>Idle delay before stopping preview (seconds)</strong></label>
                <input type="number" min="10" step="1" className="rs-input"
                  value={settings.roku_screensaver_idle_seconds || '40'}
                  onChange={e => set('roku_screensaver_idle_seconds', e.target.value)} />
                <em className="rs-sub">
                  Keep under ~55s to stay inside Roku's ~60s screensaver timeout (default 40). Minutes-long values
                  fail certification. Only applies while idle on a menu — never interrupts fullscreen playback.
                </em>
              </div>

              {(settings.roku_screensaver_idle_enabled === '0' || Number(settings.roku_screensaver_idle_seconds) > 55) && (
                <p className="rs-sub" style={{ color: '#d32f2f' }}>
                  ⚠ Current values will FAIL Roku screensaver certification. Use only for internal/demo builds.
                </p>
              )}

              <button className="cmpl-btn cmpl-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save app settings'}
              </button>
            </>
          )}
        </div>
      )}

      {tab === 'migration' && <RokuImageMigration />}
      {tab === 'validator' && <RokuFeedValidator />}
    </div>
  )
}
